import Button, { ButtonProps } from '@ui/components/atoms/button/button.atom';
import Icon from '@ui/components/atoms/icon/icon.atom';
import Typography from '@ui/components/atoms/typography/typography.atom';
import Navbar, { NavbarProps } from '@ui/components/organisms/navbar/navbar.organism';
import { clsxm } from '@ui/utils/style.util';
import { ComponentProps, PropsWithChildren, memo, useMemo } from 'react';
import useSidebarHandler, { UseSidebarHandlerProps } from './use-sidebar-handler.hook';

// #region Types
type NavbarActionProps = NavbarProps & {
  actionLabel?: string;
  actionProps?: ButtonProps;
  onActionClick?: VoidFunction;
};

export type DashboardLayoutProps = UseSidebarHandlerProps &
  PropsWithChildren<{
    activeSidebarItemMenuClassName?: string;
    additionalBottomSidebarContent?: React.ReactNode;
    additionalSidebarContent?: React.ReactNode;
    contentClassName?: string;
    contentProps?: ComponentProps<'div'>;
    navbar?: NavbarActionProps;
    onSidebarMenuClick?: (value: string) => void;
    selectedSidebarMenu?: string;
    sidebarMenuAriaLabel?: (open: boolean) => string;
  }>;
// #endregion

// #region Sub Components
function BaseNavbarAction({
  actionLabel,
  actionProps,
  onActionClick,
  toolbar: passedToolbar,
  ...rest
}: NavbarActionProps) {
  // #region Toolbar Content
  const toolbar = useMemo(
    () => (
      <>
        {passedToolbar}

        <Button
          {...actionProps}
          className={clsxm('whitespace-nowrap', actionProps?.className)}
          onClick={onActionClick ?? actionProps?.onClick}
        >
          {actionLabel}
        </Button>
      </>
    ),
    [onActionClick, actionLabel, passedToolbar, actionProps],
  );
  // #endregion

  return <Navbar {...rest} toolbar={toolbar} />;
}
BaseNavbarAction.displayName = 'NavbarAction';
const NavbarAction = memo(BaseNavbarAction);
// #endregion

function BaseDashboardLayout({
  activeSidebarItemMenuClassName,
  additionalBottomSidebarContent,
  additionalSidebarContent,
  children,
  contentClassName,
  contentProps,
  navbar,
  onSidebarMenuClick,
  selectedSidebarMenu,
  setCloseSidebarHandler,
  sidebarMenuAriaLabel,
  sidebarMenus,
}: DashboardLayoutProps) {
  // #region View Model
  const sidebar = useSidebarHandler({ setCloseSidebarHandler, sidebarMenus });
  // #endregion

  // #region Main Content
  const navbarLeftElement = useMemo(() => {
    const MenuIcon = sidebar.open ? Icon.Close : Icon.Menu;

    return (
      <button
        aria-label={sidebarMenuAriaLabel?.(sidebar.open)}
        className="md:hidden"
        onClick={sidebar.toggle}
        type="button"
      >
        <MenuIcon className="w-6 h-6 text-white-snow" />
      </button>
    );
  }, [sidebar.open, sidebar.toggle, sidebarMenuAriaLabel]);

  const renderedSidebarMenus = useMemo(
    () =>
      sidebar.menuVirtualItems.map((virtualItem) => {
        if (!sidebarMenus || virtualItem.index >= sidebarMenus.length) return null;

        const {
          className: itemClassName,
          label,
          onClick: itemOnClick,
          value,
          ...itemRest
        } = sidebarMenus[virtualItem.index];
        const handleClick = sidebar.handleSidebarMenuClick;

        return (
          <li
            key={virtualItem.index}
            ref={sidebar.menuItemRef}
            className="absolute left-0 top-0 w-full"
            data-index={virtualItem.index}
            style={{ transform: `translateY(${virtualItem.start}px)` }}
          >
            <button
              {...itemRest}
              className={clsxm(
                'w-full h-10 px-4 flex items-center rounded-full duration-200',
                selectedSidebarMenu === value && [
                  'bg-white-yellow',
                  activeSidebarItemMenuClassName,
                ],
                itemClassName,
              )}
              onClick={(event) => {
                handleClick();
                onSidebarMenuClick?.(value);
                itemOnClick?.(event);
              }}
              type="button"
            >
              <Typography.BodyOne className="text-black-charcoal truncate">
                {label}
              </Typography.BodyOne>
            </button>
          </li>
        );
      }),
    [
      activeSidebarItemMenuClassName,
      sidebar.handleSidebarMenuClick,
      sidebar.menuItemRef,
      sidebar.menuVirtualItems,
      sidebarMenus,
      selectedSidebarMenu,
      onSidebarMenuClick,
    ],
  );

  const renderedSidebar = useMemo(() => {
    if (!additionalSidebarContent && !sidebarMenus?.length) return null;

    return (
      <>
        <div
          aria-hidden
          className={clsxm(
            'transition-opacity',
            sidebar.open
              ? 'z-10 md:hidden absolute inset-0 bg-overlay/50 opacity-100'
              : 'pointer-events-none opacity-0',
          )}
          onClick={sidebar.toggle}
        />

        <div
          className="shrink-0 shadow-button z-20 overflow-hidden ease-in-out duration-300 max-md:absolute max-md:inset-y-0"
          style={sidebar.style}
        >
          <aside className="flex flex-col min-w-[220px] w-full h-full px-3 space-y-4 bg-white-teal overflow-hidden">
            {additionalSidebarContent}

            {!!sidebarMenus?.length && (
              <nav ref={sidebar.menuContainerRef} className="overflow-y-auto !mt-0 -mx-3 py-4 px-3">
                <ul className="relative w-full" style={{ height: `${sidebar.menuHeight}px` }}>
                  {renderedSidebarMenus}
                </ul>
              </nav>
            )}

            {additionalBottomSidebarContent}
          </aside>
        </div>
      </>
    );
  }, [
    additionalSidebarContent,
    sidebar.menuContainerRef,
    sidebar.menuHeight,
    sidebar.open,
    sidebar.style,
    sidebar.toggle,
    renderedSidebarMenus,
    sidebarMenus?.length,
    additionalBottomSidebarContent,
  ]);
  // #endregion

  return (
    <div className="h-full flex flex-col">
      {navbar && (
        <NavbarAction {...navbar} leftElement={renderedSidebar ? navbarLeftElement : undefined} />
      )}

      <main className="grow min-h-0 flex items-stretch relative">
        {renderedSidebar}

        <div
          {...contentProps}
          className={clsxm(
            'grow px-7 py-6 overflow-y-auto',
            contentClassName,
            contentProps?.className,
          )}
        >
          {children}
        </div>
      </main>
    </div>
  );
}
BaseDashboardLayout.displayName = 'DashboardLayout';

const DashboardLayout = memo(BaseDashboardLayout);

export default DashboardLayout;
